import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { billingAPI, userAPI } from "../services/api";
import {
  Receipt,
  Download,
  FileText,
  Wallet,
  Clock,
  Mail,
  ChevronDown,
  ChevronUp,
  Key,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat("en-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-CH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const formatSeconds = (seconds = 0) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

// ─────────────────────────────────────────────────────────────────────────────
// StatusBadge
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status, t }) => {
  const colorClass =
    status === "paid"
      ? "bg-emerald-400/10 text-emerald-300"
      : status === "overdue"
        ? "bg-rose-400/10 text-rose-300"
        : status === "sent"
          ? "bg-blue-400/10 text-blue-300"
          : "bg-white/10 text-white";

  const key = `billing.status.${status}`;
  const label = t(
    key,
    status
      ? status.charAt(0).toUpperCase() + status.slice(1)
      : t("billing.status.unknown"),
  );

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${colorClass}`}
    >
      {label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ServiceRow
// ─────────────────────────────────────────────────────────────────────────────
const ServiceRow = ({ svc, t }) => {
  const svcKey = svc.serviceType ? svc.serviceType.toLowerCase() : "unknown";
  const translationKey = `billing.detail.services.${svcKey}`;
  const svcLabel = t(
    translationKey,
    svc.serviceType || t("billing.detail.services.unknown"),
  );

  return (
    <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-4 py-2 text-sm">
      <span className="text-slate-300 capitalize">{svcLabel}</span>
      <div className="flex items-center gap-6 text-right">
        {svc.audioSeconds > 0 && (
          <span className="text-xs text-slate-400">
            {formatSeconds(svc.audioSeconds)}
          </span>
        )}
        <span className="text-xs text-slate-400">
          {svc.eventCount} {t("billing.detail.events")}
        </span>
        <span className="font-semibold text-white">
          {formatCurrency(svc.totalCost)}
        </span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TokenSection — collapsible per-token block inside invoice detail
// ─────────────────────────────────────────────────────────────────────────────
const TokenSection = ({ tb, t }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/40">
      <button
        className="flex w-full items-center justify-between px-5 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5">
            <Key className="h-4 w-4 text-cyan-300" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{tb.tokenName}</p>
            <p className="text-xs text-slate-400">
              {tb.services.length} {t("billing.detail.service")}
              {tb.services.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-base font-semibold text-white">
            {formatCurrency(tb.totalCost)}
          </p>
          {open ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </button>
      {open && (
        <div className="space-y-2 border-t border-white/5 px-5 py-3">
          {tb.services.map((svc) => (
            <ServiceRow key={svc.serviceType} svc={svc} t={t} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// InvoiceRow — expandable invoice row with lazy-loaded detail
// ─────────────────────────────────────────────────────────────────────────────
const InvoiceRow = ({ invoice, onDownload, onResend, t }) => {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !detail) {
      setLoadingDetail(true);
      try {
        const data = await billingAPI.getInvoiceDetails(
          invoice.id || invoice._id,
        );
        setDetail(data.invoice);
      } catch {
        toast.error(t("billing.errors.loadFailed"));
      } finally {
        setLoadingDetail(false);
      }
    }
    setExpanded((v) => !v);
  };

  const invoiceId = invoice.id || invoice._id;
  const invoiceNumber = invoice.invoiceNumber || invoice.number;

  return (
    <div className="rounded-2xl border border-white/5 bg-white/5 overflow-hidden">
      {/* Summary row */}
      <button
        className="flex w-full items-center gap-4 px-6 py-4 text-left hover:bg-white/5 transition-colors"
        onClick={handleExpand}
      >
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
          <Receipt className="h-5 w-5 text-white/70" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white">{invoiceNumber}</p>
          <p className="text-xs text-slate-400">
            {formatDate(invoice.createdAt)}
          </p>
        </div>
        <div className="hidden sm:block text-sm text-slate-300">
          <p>{formatDate(invoice.periodStart || invoice.period?.start)}</p>
          <p className="text-xs text-slate-400">
            → {formatDate(invoice.periodEnd || invoice.period?.end)}
          </p>
        </div>
        <div className="text-right">
          <p className="font-semibold text-white">
            {formatCurrency(invoice.totalAmount || invoice.amount)}
          </p>
          <StatusBadge status={invoice.status} t={t} />
        </div>
        <div className="flex items-center gap-2 ml-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownload(invoiceId, invoiceNumber);
            }}
            className="rounded-full border border-white/20 p-2 text-white/80 hover:text-white"
            title={t("billing.downloadPDF")}
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onResend(invoiceId, invoiceNumber);
            }}
            className="rounded-full border border-white/20 p-2 text-emerald-200 hover:text-emerald-100"
            title={t("billing.resendEmail")}
          >
            <Mail className="h-4 w-4" />
          </button>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-white/10 px-6 py-5 space-y-4 bg-slate-950/30">
          {loadingDetail ? (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            </div>
          ) : detail &&
            detail.tokenBreakdown &&
            detail.tokenBreakdown.length > 0 ? (
            <>
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("billing.detail.breakdown")}
              </p>
              <div className="space-y-3">
                {detail.tokenBreakdown.map((tb) => (
                  <TokenSection key={tb.tokenId} tb={tb} t={t} />
                ))}
              </div>
              <div className="flex flex-col gap-1 border-t border-white/10 pt-4">
                <div className="flex justify-between text-sm text-slate-300">
                  <span>{t("billing.detail.subtotal")}</span>
                  <span>{formatCurrency(detail.subtotal)}</span>
                </div>
                {detail.taxAmount > 0 && (
                  <div className="flex justify-between text-sm text-slate-300">
                    <span>{t("billing.detail.tax")}</span>
                    <span>{formatCurrency(detail.taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-semibold text-white pt-1">
                  <span>{t("billing.detail.total")}</span>
                  <span>{formatCurrency(detail.totalAmount)}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400">
              {t("billing.detail.noBreakdown")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Billing component
// ─────────────────────────────────────────────────────────────────────────────
const Billing = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const fetchBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceResponse, invoiceResponse] = await Promise.all([
        userAPI.getBalance().catch(() => null),
        billingAPI.getInvoices().catch(() => []),
      ]);
      setBalanceData(balanceResponse);
      setInvoices(
        Array.isArray(invoiceResponse?.invoices)
          ? invoiceResponse.invoices
          : Array.isArray(invoiceResponse)
            ? invoiceResponse
            : [],
      );
    } catch (error) {
      console.error("Failed to fetch billing data", error);
      toast.error(t("billing.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const monthlyUsage = balanceData?.currentMonthUsage || {};
  const audioSecondsTotal = monthlyUsage.audioSeconds || 0;
  const monthlyCost = formatCurrency(monthlyUsage.cost || 0);
  const invoicesCount = Array.isArray(invoices) ? invoices.length : 0;
  const monthlyLimitSeconds = balanceData?.monthlyLimitSeconds || 0;
  const usageProgress = monthlyLimitSeconds
    ? Math.min(100, (audioSecondsTotal / monthlyLimitSeconds) * 100)
    : null;

  const statCards = [
    {
      label: t("billing.audioProcessed"),
      value: formatSeconds(audioSecondsTotal),
      subline: t("dashboard.thisMonth"),
      icon: Clock,
    },
    {
      label: t("billing.costToDate"),
      value: monthlyCost,
      subline: t("billing.spentThisMonth"),
      icon: Wallet,
    },
    {
      label: t("billing.invoices"),
      value: invoicesCount.toString().padStart(2, "0"),
      subline: t("billing.storedArchive"),
      icon: FileText,
    },
  ];

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const blob = await billingAPI.downloadInvoicePDF(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Invoice download failed", error);
      toast.error(t("billing.errors.downloadFailed"));
    }
  };

  const resendInvoice = async (invoiceId, invoiceNumber) => {
    try {
      await billingAPI.resendInvoiceEmail(invoiceId);
      toast.success(
        t("billing.success.invoiceResent", { number: invoiceNumber }),
      );
    } catch (error) {
      console.error("Invoice resend failed", error);
      toast.error(t("billing.errors.resendFailed"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#020617] text-white flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-16 left-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-[-10%] right-0 h-[26rem] w-[26rem] rounded-full bg-indigo-500/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.12)_1px,_transparent_0)] [background-size:34px_34px]" />
        </div>
      </div>

      <div className="relative z-10 px-4 pt-6 pb-12 sm:px-8 lg:px-12 space-y-8">
        {/* Header */}
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white">
                {t("billing.title")}
              </h1>
              <p className="mt-2 text-sm text-slate-300 max-w-2xl">
                {t("billing.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-lg shadow-black/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {stat.label}
                    </p>
                    <p className="mt-3 text-3xl font-semibold text-white">
                      {stat.value}
                    </p>
                    <p className="text-xs text-slate-400">{stat.subline}</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
                    <Icon className="h-5 w-5 text-cyan-200" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Monthly usage bar */}
        {usageProgress !== null && (
          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
            <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
              {t("billing.usageSnapshot")}
            </p>
            <h3 className="mt-2 text-2xl font-semibold text-white">
              {t("billing.monthlyConsumption")}
            </h3>
            <p className="text-sm text-slate-300">
              {t("billing.monitorUsage")}
            </p>
            <div className="mt-6">
              <div className="flex justify-between text-xs uppercase tracking-[0.4em] text-slate-500">
                <span>{t("billing.usageConsumed")}</span>
                <span>
                  {formatSeconds(audioSecondsTotal)} /{" "}
                  {formatSeconds(monthlyLimitSeconds)}
                </span>
              </div>
              <div className="mt-3 h-3 w-full rounded-full bg-white/5">
                <div
                  className="h-3 rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500"
                  style={{ width: `${usageProgress.toFixed(1)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Invoice list */}
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-2xl font-semibold text-white">
                {t("billing.recentInvoices")}
              </h3>
            </div>
          </div>

          {invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id || invoice._id}
                  invoice={invoice}
                  onDownload={downloadInvoice}
                  onResend={resendInvoice}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-white/20 bg-white/5">
                <Receipt className="h-8 w-8 text-white/70" />
              </div>
              <h3 className="mt-6 text-2xl font-semibold">
                {t("billing.noInvoices")}
              </h3>
              <p className="mt-2 max-w-md text-sm text-slate-300">
                {t("billing.noInvoicesDesc")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
